import { useState, useEffect } from 'react';
import { useQuery } from './QueryContext';
import styles from './Creators.module.css';
import Header from './components/Header';
import SideBar from './components/SideBar';
import QueryPlatform from './components/QueryPlatform';
import QueryContainer from './components/QueryContainer';
import ListTitle from './components/ListTitle';
import ListHeader from './components/ListHeader';
import { downloadJson, sanitizeFilenamePart } from './utils/downloadJson';
import { downloadBlob } from './utils/downloadFile';
import { toCsv } from './utils/toCsv';

const proxyImg = (url) => {
  if (!url) return '';
  if (/scontent[^/]*\.cdninstagram\.com|\.fbcdn\.net/.test(url)) {
    return `/api/proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const formatNumber = (num) => {
  if (!num) return '-';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num;
};

const getCountryFlag = (countryCode) => {
  if (!countryCode) return '';
  return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
};

const mapCreatorItem = (item, platform, selectedCountry) => ({
  rank: item.rank,
  nickname: item.nickname || '',
  country: platform === 'youtube' ? selectedCountry : (item.country || ''),
  followers: item.followers || 0,
  engagement: item.engagement || 0,
  posts: item.posts || 0,
  profile_url: item.profileUrl || '',
  avatar: item.avatar || '',
  top_videos: (item.top_videos || []).map((v) => ({
    url: v.url || '',
    thumbnail: v.thumbnail || '',
  })),
});

const CreatorRankItem = ({ rank, avatar, nickname, profileUrl, country, followers, engagement, posts, topVideos }) => {
  const handleProfileClick = () => {
    if (profileUrl) window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles.rankRow}>
      <div className={styles.rankCell}>
        <span className={styles.rankNum}>{rank}</span>
      </div>
      <div
        className={styles.creatorCell}
        onClick={handleProfileClick}
        style={{ cursor: profileUrl ? 'pointer' : 'default' }}
      >
        {avatar
          ? <img src={proxyImg(avatar)} alt="" className={styles.avatar} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; }} />
          : <div className={styles.avatarPlaceholder} />}
        <span className={styles.nickname}>{nickname || '-'}</span>
      </div>
      <div className={styles.countryCell}>
        {country && <img src={getCountryFlag(country)} alt={country} className={styles.flag} />}
      </div>
      <div className={styles.followersCell}>{formatNumber(followers)}</div>
      <div className={styles.engagementCell}>{formatNumber(engagement)}</div>
      <div className={styles.postsCell}>{formatNumber(posts)}</div>
      <div className={styles.topVideosCell}>
        {(topVideos || []).slice(0, 5).map((video, i) => (
          <div
            key={i}
            className={styles.videoThumb}
            onClick={() => video.url && window.open(video.url, '_blank', 'noopener,noreferrer')}
            style={{ cursor: video.url ? 'pointer' : 'default' }}
          >
            {video.thumbnail && <img src={proxyImg(video.thumbnail)} alt="" className={styles.thumbImg} onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
          </div>
        ))}
      </div>
      <div className={styles.rowDivider} />
    </div>
  );
};

const Creators = () => {
  const [creatorsList, setCreatorsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ minDate: null, maxDate: null });
  const { selectedPlatform, setSelectedPlatform, selectedCountry, selectedCategory, selectedSubCategory, selectedDate, setSelectedDate } = useQuery();

  useEffect(() => {
    if (!selectedPlatform || !selectedCountry || !selectedCategory) return;
    let cancelled = false;
    fetch(`/api/dates?platform=${encodeURIComponent(selectedPlatform)}&country=${encodeURIComponent(selectedCountry)}&category=${encodeURIComponent(selectedCategory)}&subcategory=${encodeURIComponent(selectedSubCategory)}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d.success) return;
        setDateRange({
          minDate: d.data.minDate ? new Date(d.data.minDate) : null,
          maxDate: d.data.maxDate ? new Date(d.data.maxDate) : null,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedPlatform, selectedCountry, selectedCategory, selectedSubCategory]);

  useEffect(() => {
    let cancelled = false;
    const fetchCreatorsList = async () => {
      if (!selectedPlatform || !selectedCountry || !selectedCategory) {
        if (cancelled) return;
        setCreatorsList([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const dateParam = selectedDate
          ? `&date=${selectedDate.toISOString().slice(0, 10)}`
          : '';
        const res = await fetch(
          `/api/creators?platform=${encodeURIComponent(selectedPlatform)}&category=${encodeURIComponent(selectedCategory)}&subcategory=${encodeURIComponent(selectedSubCategory)}&country=${encodeURIComponent(selectedCountry)}${dateParam}`
        );
        if (cancelled) return;
        if (!res.ok) throw new Error('Failed to fetch creators');
        const data = await res.json();
        if (cancelled) return;
        if (data.success) {
          setCreatorsList(data.data.items || []);
          if (data.data.date) {
            const apiDate = new Date(data.data.date).toISOString().slice(0, 10);
            const curDate = selectedDate ? selectedDate.toISOString().slice(0, 10) : null;
            if (apiDate !== curDate) setSelectedDate(new Date(data.data.date));
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching creators:', err);
        setError(err.message);
        setCreatorsList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCreatorsList();
    return () => { cancelled = true; };
  }, [selectedPlatform, selectedCountry, selectedCategory, selectedSubCategory, selectedDate]);

  const handleDownload = (format) => {
    const items = creatorsList.map((item) =>
      mapCreatorItem(item, selectedPlatform, selectedCountry)
    );
    const dateStr = selectedDate
      ? selectedDate.toISOString().slice(0, 10)
      : 'unknown';
    const payload = {
      page: 'creators',
      platform: selectedPlatform,
      category: selectedCategory,
      country: selectedCountry,
      date: dateStr,
      exported_at: new Date().toISOString(),
      count: items.length,
      items,
    };
    const baseName = [
      'creators',
      sanitizeFilenamePart(selectedPlatform),
      sanitizeFilenamePart(selectedCategory),
      sanitizeFilenamePart(selectedCountry),
      sanitizeFilenamePart(dateStr),
    ].join('-');
    if (format === 'csv') {
      downloadBlob(toCsv(payload.items), baseName + '.csv', 'text/csv;charset=utf-8');
    } else {
      downloadJson(payload, baseName + '.json');
    }
  };

  return (
    <div className={styles.page}>
      <Header />
      <div className={styles.mainContent}>
        <SideBar />
        <div className={styles.body}>
          <QueryPlatform onPlatformChange={setSelectedPlatform} />
          <QueryContainer />
          <ListTitle
            title="Top 100 Short Form Video Creators"
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            minDate={dateRange.minDate}
            maxDate={dateRange.maxDate}
            onDownload={handleDownload}
            disabled={loading || creatorsList.length === 0}
          />
          <ListHeader columns={[
            { label: 'Rank',       className: styles.rankCell },
            { label: 'Creator',    className: styles.creatorCell },
            { label: 'Country',    className: styles.countryCell },
            { label: 'Followers',  className: styles.followersCell },
            { label: 'Engagement', className: styles.engagementCell },
            { label: 'Posts',      className: styles.postsCell },
            { label: 'Top Videos', className: styles.topVideosCell },
          ]} />
          {loading ? (
            <div className={styles.status}>Loading...</div>
          ) : error ? (
            <div className={styles.status}>No data available.</div>
          ) : creatorsList.length === 0 ? (
            <div className={styles.status}>No data available.</div>
          ) : (
            creatorsList.map((item, i) => (
              <CreatorRankItem
                key={i}
                rank={item.rank}
                avatar={item.avatar}
                nickname={item.nickname}
                profileUrl={item.profileUrl}
                country={selectedPlatform === 'youtube' ? selectedCountry : item.country || ''}
                followers={item.followers}
                engagement={item.engagement}
                posts={item.posts}
                topVideos={item.top_videos}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Creators;
